import React, { useState, useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { crearCurso } from "../../../../utils/acciones";
import {
  buildContenidoPayloadForSave,
  applyCourseTabNameInput,
  findTemarioContentKey,
  getNamedCourseTabs,
  getCourseTabsSaveBlocker,
  hasDuplicateCourseTabs,
  normalizeCourseContenido,
  normalizeCourseTabName,
  resizeUniqueTabKeys,
} from "../../../../utils/normalizeCourseShape";
import { resolveSelectedCourseTab } from "../../../../utils/resolveSelectedCourseTab";
import {
  listPathOptions,
} from "../../../../utils/temarioTree";
import { API_URL } from "../../../../config";
import { remapPestanaListAfterRename } from "./remapPestanaKeys";
import "./CrearCurso.css";
import "./VistaCursosAdmin.css";
import CourseDownloadableToggle from "../../../../components/CourseDownloadableToggle";
import ResponsiveActionSidebar from "../../../../components/ResponsiveActionSidebar";
import CourseTestPreviewModal from "../../../../components/CourseTestPreviewModal";
import TemarioContenidoPreview from "../../../../components/TemarioContenidoPreview";
import TemarioContenidoEditor from "../../../../components/TemarioContenidoEditor";
import AdminDocsTestsByTab from "../../../../components/AdminDocsTestsByTab";
import { confirmDeleteTestQuestion } from "../../../../utils/confirmDeleteTestQuestion";
import { uploadTestQuestionImage } from "../../../../utils/uploadTestQuestionImage";
import { getMessage } from "../../../../data/locales";
import ViewportSelect from "../../../../components/ViewportSelect";
import { getCreateCourseSidebarTabs } from "../../../../utils/courseEditorSidebarLabels";
import { removeArchivoFromTemarioState } from "../../../../utils/courseTemarioArchivoRemove";
import {
  filterNonBlankTestPreguntas,
  validateTestPreguntasForSave,
} from "./validateTestPreguntasForSave";
import { normalizeCourseDurationInput } from "../../../../utils/formatCourseDurationLabel";
import { isCourseDescriptionEmpty } from "../../../../utils/courseDescriptionHtml";
import CourseDescriptionEditor from "../../../../components/CourseDescriptionEditor";
import CourseDescriptionContent from "../../../../components/CourseDescriptionContent";
import {
  formatTestQuestionSidebarPreview,
  formatTestQuestionSidebarTitle,
} from "../../../../utils/formatTestQuestionSidebarLabel";
import { moveCourseTest, resolveMovedEditingIndex } from "../../../../utils/courseTestsReorder";
import {
  getCourseMaterialOversizedSelectionMessage,
  isCourseMaterialFileOversized,
} from "../../../../constants/courseMaterialUploadLimits";
import EditarCursoDescuentosTab from "./EditarCursoDescuentosTab";
import {
  docAddFailureMessage,
  getDocAddValidationMessage,
  mergeArchivoIntoBloqueLista,
} from "./editarCursoDocumentAdd";
import { uploadCourseFileWithProgress } from "./uploadCourseFileWithProgress";
import { uploadPhaseMessage } from "./editarCursoUiStrings";
import {
  DOC_UPLOAD_ACCEPT_ATTR,
  fileMatchesCourseDocUploadAccept,
} from "./courseDocUploadAccept";

const TEMARIO_DEPTH_LABELS = ["Bloque", "Tema", "Unidad"];
const SUCCESS_MESSAGE_AUTO_HIDE_MS = 4500;

const DEFAULT_RESPUESTAS_PLANTILLA = Object.freeze(["", "", "", ""]);

function createEmptyQuestion() {
  return {
    pregunta: "",
    respuestas: [...DEFAULT_RESPUESTAS_PLANTILLA],
    correcta: null,
    explicacion: "",
  };
}

function ensurePreguntaAtIndex(preguntas, index) {
  const nuevas = [...preguntas];
  while (nuevas.length <= index) {
    nuevas.push(createEmptyQuestion());
  }
  if (!nuevas[index]) {
    nuevas[index] = createEmptyQuestion();
    return nuevas;
  }
  const existing = nuevas[index];
  const respuestas = normalizeRespuestas(existing.respuestas);
  nuevas[index] = { ...existing, respuestas };
  return nuevas;
}

function normalizeRespuestas(raw) {
  const respuestas = Array.isArray(raw) ? [...raw] : [...DEFAULT_RESPUESTAS_PLANTILLA];
  while (respuestas.length < 4) respuestas.push("");
  return respuestas;
}

async function uploadCourseImageIfAny(file) {
  if (!file) {
    return { url: "", storageKey: "" };
  }
  const base64 = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error("Error al leer imagen"));
    reader.readAsDataURL(file);
  });
  const res = await fetch(`${API_URL}/subir-imagen-curso`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({
      nombre: file.name,
      tipo: file.type,
      contenidoBase64: base64,
    }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data.url) {
    const err = typeof data?.error === "string" && data.error.trim() ? data.error : res.statusText;
    throw new Error(err || "Falló la subida de la imagen al servidor.");
  }
  const storageKey = typeof data.nombre === "string" ? data.nombre : "";
  return { url: data.url, storageKey };
}

async function deleteUploadedCourseImageQuiet(storageKey) {
  if (!storageKey || typeof storageKey !== "string" || !storageKey.startsWith("imagenes-curso/")) {
    return;
  }
  try {
    const res = await fetch(`${API_URL}/eliminar-imagen-curso`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ key: storageKey }),
    });
    if (!res.ok) {
      console.warn("deleteUploadedCourseImageQuiet: HTTP", res.status);
    }
  } catch (e) {
    console.warn("deleteUploadedCourseImageQuiet:", e);
  }
}

const CrearCurso = () => {
  const [titulo, setTitulo] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [duracion, setDuracion] = useState("");
  const [precio, setPrecio] = useState("");
  const [descuento, setDescuento] = useState("");
  const [codigoDescuento, setCodigoDescuento] = useState("");
  const [precioFinal, setPrecioFinal] = useState("");
  const [mensaje, setMensaje] = useState("");
  const mensajeRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState("crearCurso");
  const [imagenCurso, setImagenCurso] = useState(null);
  const [imagenCursoPreviewUrl, setImagenCursoPreviewUrl] = useState(null);
  const [preguntas, setPreguntas] = useState([
    {
      pregunta: "",
      respuestas: ["", "", "", ""],
      correcta: null,
      explicacion: "",
    },
  ]);
  const [preguntaSeleccionada, setPreguntaSeleccionada] = useState(0);
  const [bloquesContenido, setBloquesContenido] = useState({});
  const [archivoSeleccionado, setArchivoSeleccionado] = useState(null);
  const [bloqueSeleccionado, setBloqueSeleccionado] = useState("");
  const [subseccion, setSubseccion] = useState("");
  const [rutaDestinoKey, setRutaDestinoKey] = useState("");
  const [rutaPadreKey, setRutaPadreKey] = useState("");
  const [posicion, setPosicion] = useState("debajo");
  const [tipoInsert, setTipoInsert] = useState("nuevo"); // nuevo o existente subsección
  const [tests, setTests] = useState([]);
  const [testAEliminarIndex, setTestAEliminarIndex] = useState(null);
  const [mostrarConfirmacion, setMostrarConfirmacion] = useState(false);
  const [modoEdicion, setModoEdicion] = useState(false); // Si estamos editando un test
  const [indexEdicion, setIndexEdicion] = useState(null); // Índice del test que se está editando
  const [mostrarVistaPrevia, setMostrarVistaPrevia] = useState(false);
  const [testParaVistaPrevia, setTestParaVistaPrevia] = useState(null);
  const [borradorCargado, setBorradorCargado] = useState(false);
  const skipBorradorPersistRef = useRef(false);
  const [mostrarModalBorrarBorrador, setMostrarModalBorrarBorrador] = useState(false);
  const [pestanasCurso, setPestanasCurso] = useState([]); // ejemplo: ["Teoría", "Repaso", "Examen Final"]
  const lastUniqueTabKeysRef = useRef([]);
  const [pestanaVistaPrevia, setPestanaVistaPrevia] = useState(pestanasCurso[0] || "");
  const [esAbierto, setEsAbierto] = useState(false);
  const [fechaCierre, setFechaCierre] = useState("");
  const [contenidoDescargable, setContenidoDescargable] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState("idle");
  const [lastUploadError, setLastUploadError] = useState("");
  const currentUploadRef = useRef(null);
  const lastAttemptedFileRef = useRef(null);
  const mountedRef = useRef(true);
  const [actionBarVisible, setActionBarVisible] = useState(true);
  useEffect(() => {
    try {
      localStorage.setItem("crearCurso_actionBarVisible", JSON.stringify(actionBarVisible));
    } catch {
      // ignore
    }
  }, [actionBarVisible]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!imagenCurso) {
      setImagenCursoPreviewUrl(null);
      return;
    }
    const objectUrl = URL.createObjectURL(imagenCurso);
    setImagenCursoPreviewUrl(objectUrl);
    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [imagenCurso]);
  const [testActual, setTestActual] = useState({
    titulo: "",
    tipo: "temario",
    tiempo: 0,
    preguntas: [
      {
        pregunta: "",
        respuestas: ["", "", "", ""],
        correcta: null,
        explicacion: "",
      },
    ],
    pestanas: [] // ✅ añadimos esta propiedad
  });

  const hasUnsavedChanges =
    titulo.trim() !== "" ||
    !isCourseDescriptionEmpty(descripcion) ||
    duracion.trim() !== "" ||
    precio !== "" ||
    descuento !== "" ||
    codigoDescuento.trim() !== "" ||
    imagenCurso !== null ||
    tests.length > 0 ||
    pestanasCurso.some((name) => String(name || "").trim() !== "") ||
    Object.values(bloquesContenido).some((items) => Array.isArray(items) && items.length > 0) ||
    testActual.titulo.trim() !== "" ||
    preguntas.some((item) =>
      String(item?.pregunta || "").trim() !== "" ||
      String(item?.explicacion || "").trim() !== "" ||
      (Array.isArray(item?.respuestas) && item.respuestas.some((answer) => String(answer || "").trim() !== ""))
    );

  useEffect(() => {
    if (!hasUnsavedChanges) return undefined;

    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    const handleDocumentLink = (event) => {
      const link = event.target instanceof Element ? event.target.closest("a[href]") : null;
      if (!link || link.target === "_blank" || event.defaultPrevented) return;
      if (!globalThis.confirm("Tienes cambios sin guardar. ¿Quieres salir y descartarlos?")) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      localStorage.removeItem("borradorCurso");
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    document.addEventListener("click", handleDocumentLink, true);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      document.removeEventListener("click", handleDocumentLink, true);
    };
  }, [hasUnsavedChanges]);
  
 useEffect(() => {
  if (pestanasCurso.length > 0 && (!pestanaVistaPrevia || !pestanasCurso.includes(pestanaVistaPrevia))) {
    setPestanaVistaPrevia(pestanasCurso[0]);
  }
}, [pestanasCurso, pestanaVistaPrevia]);

  useEffect(() => {
    setBloquesContenido((prev) => {
      const next = { ...prev };
      pestanasCurso.forEach((p) => {
        const k = (p ?? "").trim();
        if (!k) return;
        if (!next[k]) next[k] = [];
      });
      return next;
    });
  }, [pestanasCurso]);

  useEffect(() => {
    setBloqueSeleccionado((current) =>
      resolveSelectedCourseTab(current, pestanasCurso, bloquesContenido),
    );
  }, [pestanasCurso, bloquesContenido]);

  useEffect(() => {
    if (tipoInsert !== "existente") {
      return;
    }
    const list = bloquesContenido[bloqueSeleccionado] || [];
    const opts = listPathOptions(list, TEMARIO_DEPTH_LABELS);
    const valid = new Set(opts.map((o) => o.pathKey));
    setRutaDestinoKey((cur) => (cur && valid.has(cur) ? cur : ""));
  }, [bloqueSeleccionado, bloquesContenido, tipoInsert]);

  useEffect(() => {
    if (tipoInsert !== "nuevo") {
      return;
    }
    const list = bloquesContenido[bloqueSeleccionado] || [];
    const opts = listPathOptions(list, TEMARIO_DEPTH_LABELS);
    const validParents = new Set(["", ...opts.map((o) => o.pathKey)]);
    setRutaPadreKey((cur) => (validParents.has(cur) ? cur : ""));
  }, [bloqueSeleccionado, bloquesContenido, tipoInsert]);



  useEffect(() => {
    const precioNum = Number.parseFloat(precio);
    const descuentoNum = Number.parseFloat(descuento);

    if (!Number.isNaN(precioNum) && descuentoNum > 0 && codigoDescuento.trim() !== "") {
      const nuevoPrecio = precioNum - (precioNum * descuentoNum) / 100;
      setPrecioFinal(nuevoPrecio.toFixed(2));
    } else {
      setPrecioFinal(precioNum || "");
    }
  }, [precio, descuento, codigoDescuento]);


  useEffect(() => {
    if (location.state?.startFresh === true) {
      localStorage.removeItem("borradorCurso");
      navigate(location.pathname, { replace: true, state: {} });
      setBorradorCargado(true);
      return;
    }
    const borradorGuardado = localStorage.getItem("borradorCurso");
    if (borradorGuardado) {
      try {
        const datos = JSON.parse(borradorGuardado);
        console.log("🧠 Cargando borrador del curso desde localStorage...", datos);
        setTitulo(datos.titulo || "");
        setDescripcion(datos.descripcion || "");
        setDuracion(String(datos.duracion ?? "").trim());
        setPrecio(datos.precio || "");
        setDescuento(datos.descuento || "");
        const ta = datos.testActual && typeof datos.testActual === "object" ? datos.testActual : {};
        const rawPreguntasPorIntento = ta.preguntasPorIntento;
        const preguntasPorIntento =
          typeof rawPreguntasPorIntento === "number" &&
          Number.isFinite(rawPreguntasPorIntento) &&
          rawPreguntasPorIntento > 0
            ? Math.max(1, Math.floor(rawPreguntasPorIntento))
            : null;
        setTestActual({
          titulo: typeof ta.titulo === "string" ? ta.titulo : "",
          tipo: ta.tipo === "examen" ? "examen" : "temario",
          tiempo: typeof ta.tiempo === "number" && !Number.isNaN(ta.tiempo) ? ta.tiempo : 0,
          preguntasPorIntento,
          preguntas: Array.isArray(ta.preguntas) ? ta.preguntas : [],
          pestanas: Array.isArray(ta.pestanas) ? ta.pestanas : [],
        });
        setPreguntas(datos.preguntas || []);
        setTests(datos.tests || []);
        setBloquesContenido(
          normalizeCourseContenido(
            datos.bloquesContenido && typeof datos.bloquesContenido === "object" ? datos.bloquesContenido : {},
          ),
        );
        setPestanasCurso(datos.pestanasCurso || []);
        lastUniqueTabKeysRef.current = (datos.pestanasCurso || []).map((tab) => normalizeCourseTabName(tab));
        setEsAbierto(datos.esAbierto === true);
        setFechaCierre(datos.fechaCierre || "");
        setContenidoDescargable(datos.contenidoDescargable === true);
      } catch (error) {
        console.error("❌ Error al cargar el borrador del curso:", error);
        localStorage.removeItem("borradorCurso");
        setMensaje("⚠️ El borrador guardado estaba dañado y se ha eliminado.");
      }
    }
    setBorradorCargado(true); // ← importante
  }, [location.pathname, location.state, navigate]);
  
  useEffect(() => {
    if (!borradorCargado) return;
    if (skipBorradorPersistRef.current) {
      skipBorradorPersistRef.current = false;
      return;
    }

    const borradorCurso = {
      titulo,
      descripcion,
      duracion: normalizeCourseDurationInput(duracion),
      precio,
      descuento,
      testActual,
      preguntas,
      tests,
      bloquesContenido,
      pestanasCurso,
      esAbierto,
      fechaCierre,
      contenidoDescargable,
    };
    localStorage.setItem("borradorCurso", JSON.stringify(borradorCurso));
  }, [borradorCargado, titulo, descripcion, duracion, precio, descuento, testActual, preguntas, tests, bloquesContenido, pestanasCurso, esAbierto, fechaCierre, contenidoDescargable]);

  useEffect(() => {
    if (!mensaje || !mensaje.trim().startsWith("✅")) {
      return undefined;
    }

    const frameId = requestAnimationFrame(() => {
      mensajeRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    });
    const timeoutId = setTimeout(() => {
      setMensaje((current) => (current === mensaje ? "" : current));
    }, SUCCESS_MESSAGE_AUTO_HIDE_MS);

    return () => {
      cancelAnimationFrame(frameId);
      clearTimeout(timeoutId);
    };
  }, [mensaje]);
  
  

  useEffect(() => {
    setActiveTab("crearCurso");
  }, []);

  
  const textareaRef = useRef(null);
  
  const preguntaActualTexto = preguntas[preguntaSeleccionada]?.pregunta;

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto"; // reset
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`; // auto-expande
    }
  }, [preguntaActualTexto]);

  useEffect(() => {
    if (preguntaSeleccionada >= preguntas.length) {
      setPreguntaSeleccionada(Math.max(0, preguntas.length - 1));
    }
  }, [preguntas, preguntaSeleccionada]);

  const cleanupStateForRemovedCourseTabs = (removedNames) => {
    const names = removedNames.map((n) => (typeof n === "string" ? n.trim() : "")).filter(Boolean);
    if (names.length === 0) {
      return;
    }
    const removedSet = new Set(names);
    const emptyTestDraft = () => ({
      titulo: "",
      tipo: "temario",
      tiempo: 0,
      preguntas: [
        {
          pregunta: "",
          respuestas: ["", "", "", ""],
          correcta: null,
          explicacion: "",
        },
      ],
      pestanas: [],
    });
    setBloquesContenido((prev) => {
      const next = { ...prev };
      names.forEach((name) => {
        delete next[name];
      });
      return next;
    });
    setTests((prev) =>
      prev
        .map((t) => ({
          ...t,
          pestanas: Array.isArray(t.pestanas)
            ? t.pestanas.filter((p) => !removedSet.has((p ?? "").trim()))
            : [],
        }))
        .filter((t) => Array.isArray(t.pestanas) && t.pestanas.length > 0),
    );
    let resetTestDraft = false;
    setTestActual((prev) => {
      const nextPestanas = Array.isArray(prev.pestanas)
        ? prev.pestanas.filter((p) => !removedSet.has((p ?? "").trim()))
        : [];
      if (nextPestanas.length === 0) {
        resetTestDraft = true;
        return emptyTestDraft();
      }
      return {
        ...prev,
        pestanas: nextPestanas,
      };
    });
    if (resetTestDraft) {
      setPreguntas([
        {
          pregunta: "",
          respuestas: ["", "", "", ""],
          correcta: null,
          explicacion: "",
        },
      ]);
      setPreguntaSeleccionada(0);
      setModoEdicion(false);
      setIndexEdicion(null);
      setMensaje("⚠️ La pestaña asignada al borrador de test fue eliminada; el borrador se ha reiniciado.");
    }
    setPestanaVistaPrevia((cur) => (removedSet.has((cur ?? "").trim()) ? "" : cur));
  };

  const handleGuardarCurso = async (e) => {
    e.preventDefault();
    setMensaje("");

    const trimmedDuracion = normalizeCourseDurationInput(duracion);

    if (!titulo || isCourseDescriptionEmpty(descripcion) || !trimmedDuracion) {
      setMensaje("⚠️ Debes completar los datos del curso (título, descripción y duración) antes de guardar.");
      setActiveTab("crearCurso");
      return;
    }

    const tabsSaveBlocker = getCourseTabsSaveBlocker(pestanasCurso);
    if (tabsSaveBlocker === "unnamed") {
      setMensaje(`⚠️ ${getMessage("courseEditor.unnamedTabsSaveWarning")}`);
      setActiveTab("crearCurso");
      return;
    }
    if (tabsSaveBlocker === "duplicate") {
      setMensaje(`⚠️ ${getMessage("courseEditor.duplicateTabsSaveWarning")}`);
      setActiveTab("crearCurso");
      return;
    }

    if (precio === "" || descuento === "") {
      setMensaje("⚠️ El precio y el descuento no pueden estar vacíos.");
      return;
    }

    const precioNum = Number.parseFloat(precio);
    const descuentoNum = Number.parseFloat(descuento);

    if (Number.isNaN(precioNum) || Number.isNaN(descuentoNum)) {
      setMensaje("⚠️ El precio y descuento deben ser válidos.");
      return;
    }

    if (precioNum < 0 || descuentoNum < 0 || descuentoNum > 100) {
      setMensaje("⚠️ El precio y descuento deben ser válidos.");
      return;
    }

    const precioFinal = precioNum - (precioNum * (descuentoNum / 100));

    let urlImagenSubida = "";
    let imagenStorageKey = "";
    try {
      const uploaded = await uploadCourseImageIfAny(imagenCurso);
      urlImagenSubida = uploaded.url;
      imagenStorageKey = uploaded.storageKey;
    } catch (error) {
      console.error("❌ Error al subir imagen:", error);
      setMensaje(`❌ ${error instanceof Error ? error.message : "Falló la subida de la imagen al servidor."}`);
      return;
    }

    try {
      const bloquesLimpios = buildContenidoPayloadForSave(bloquesContenido, pestanasCurso);
      console.log("🖼️ URL de la imagen subida:", urlImagenSubida);
      const cursoData = {
        titulo,
        descripcion,
        duracion: trimmedDuracion,
        precio: precioNum,
        descuento: descuentoNum,
        precioFinal,
        codigoDescuento,
        contenido: bloquesLimpios,
        tests,
        pestanasCurso: getNamedCourseTabs(pestanasCurso),
        imagen: urlImagenSubida,
        publicadoAlumnos: false,
        esAbierto,
        fechaCierre: fechaCierre.trim() === "" ? null : fechaCierre,
        contenidoDescargable,
      };

      console.log("📦 Datos que se envían al backend:", cursoData);
      const respuesta = await crearCurso(cursoData);

      if (respuesta?.curso) {
        localStorage.removeItem("borradorCurso");
        const rawId = respuesta.curso._id ?? respuesta.curso.id;
        const newId = rawId != null ? String(rawId) : "";
        if (newId) {
          navigate(`/admin/editar-curso/${newId}`, {
            replace: true,
            state: { avisoCursoCreado: true },
          });
        } else {
          setMensaje("✅ Curso creado con éxito.");
          setTitulo("");
          setDescripcion("");
          setDuracion("");
          setPrecio("");
          setDescuento("");
          setPreguntas([
            {
              pregunta: "",
              respuestas: ["", "", "", ""],
              correcta: null,
              explicacion: "",
            },
          ]);
          setBloquesContenido({});
        }
      } else {
        await deleteUploadedCourseImageQuiet(imagenStorageKey);
        setMensaje("❌ Error en la respuesta del servidor.");
      }
    } catch (error) {
      await deleteUploadedCourseImageQuiet(imagenStorageKey);
      console.error("Error al crear el curso:", error);
      setMensaje(`❌ Error: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  const handleMoveTest = (testIndex, delta) => {
    const nextTests = moveCourseTest(tests, testIndex, delta);
    if (!nextTests) {
      return;
    }
    setTests(nextTests);
    setIndexEdicion((currentIndex) => resolveMovedEditingIndex(currentIndex, testIndex, delta));
    setMensaje("↕️ Orden de tests actualizado.");
  };

  const resetUploadUi = () => {
    setUploadProgress(0);
    setUploadStatus("idle");
    setLastUploadError("");
    lastAttemptedFileRef.current = null;
  };

  const handleSelectedDocFile = (file) => {
    if (!file) {
      setArchivoSeleccionado(null);
      resetUploadUi();
      return;
    }
    if (isCourseMaterialFileOversized(file)) {
      const msg = getCourseMaterialOversizedSelectionMessage();
      setArchivoSeleccionado(null);
      lastAttemptedFileRef.current = null;
      setUploadProgress(0);
      setUploadStatus("error");
      setLastUploadError(msg);
      setMensaje(`⚠️ ${msg}`);
      return;
    }
    if (!fileMatchesCourseDocUploadAccept(file)) {
      setArchivoSeleccionado(null);
      resetUploadUi();
      setMensaje("⚠️ Formato no admitido. Usa PDF, Office, audio o vídeo.");
      return;
    }
    setArchivoSeleccionado(file);
    setUploadProgress(0);
    setUploadStatus("idle");
    setLastUploadError("");
    lastAttemptedFileRef.current = null;
  };

  const handleAddDoc = async (fileOverride = null) => {
    const selectedFile = fileOverride || archivoSeleccionado;
    const bloqueKey = (bloqueSeleccionado ?? "").trim();
    const listaPrev = bloquesContenido[bloqueKey] || [];
    const validationMessage = getDocAddValidationMessage({
      selectedFile,
      bloqueSeleccionado: bloqueKey,
      tipoInsert,
      rutaDestinoKey,
      listaActual: listaPrev,
      subseccionTrim: subseccion.trim(),
    });
    if (validationMessage) {
      setMensaje(validationMessage);
      return;
    }
    if (!fileMatchesCourseDocUploadAccept(selectedFile)) {
      setMensaje("⚠️ Formato no admitido. Usa PDF, Office, audio o vídeo.");
      return;
    }

    try {
      const data = await uploadCourseFileWithProgress(selectedFile, API_URL, {
        mountedRef,
        currentUploadRef,
        lastAttemptedFileRef,
        setUploadProgress,
        setUploadStatus,
        setLastUploadError,
        setMensaje,
      });
      if (typeof data.url !== "string" || data.url.length === 0) {
        setUploadStatus("error");
        setLastUploadError("El servidor no devolvió un enlace válido para el archivo.");
        setMensaje("❌ El servidor no devolvió un enlace válido para el archivo.");
        return;
      }
      const storageNombre =
        typeof data.nombre === "string" && data.nombre.length > 0
          ? data.nombre
          : selectedFile.name;
      const archivo = { nombre: storageNombre, tipo: selectedFile.type, url: data.url };
      const lista = [...(Array.isArray(bloquesContenido[bloqueKey]) ? bloquesContenido[bloqueKey] : [])];
      const nextList = mergeArchivoIntoBloqueLista({
        tipoInsert,
        rutaDestinoKey,
        rutaPadreKey,
        subseccionTrim: subseccion.trim(),
        posicion,
        lista,
        archivo,
      });
      if (!nextList) {
        setMensaje(docAddFailureMessage(tipoInsert));
        return;
      }
      setMensaje("✅ Archivo agregado correctamente.");
      setBloquesContenido((prev) => ({ ...prev, [bloqueKey]: nextList }));
      setArchivoSeleccionado(null);
    } catch (err) {
      const detail = err instanceof Error ? err.message : "Error desconocido";
      console.error("Error al subir archivo:", err);
      setMensaje(`❌ Error al subir archivo: ${detail}`);
    }
  };

  const handleCancelCourseCreation = () => {
    if (
      hasUnsavedChanges &&
      !globalThis.confirm("Tienes cambios sin guardar. ¿Quieres cancelar y descartarlos?")
    ) {
      return;
    }
    skipBorradorPersistRef.current = true;
    localStorage.removeItem("borradorCurso");
    navigate("/GestionCursos");
  };

  return (
    <div className="cc-container">
      
      <div className={`cc-main-container${actionBarVisible ? "" : " cc-main-container--panel-hidden"}`}>
        {actionBarVisible && (
        <ResponsiveActionSidebar
          id="course-actions-sidebar"
          className="cc-sidebar"
          openClassName="cc-sidebar--open"
          ariaLabel="Acciones de creación de curso"
        >
        <div className="cc-sidebar-header">
          <h4>Acciones</h4>
          <button
            type="button"
            className="cc-action-bar-toggle cc-action-bar-toggle--hide"
            onClick={() => setActionBarVisible(false)}
            title="Ocultar menú lateral"
            aria-label="Ocultar menú lateral"
          >
            <span className="cc-action-bar-toggle__icon" aria-hidden="true">&#9664;</span>
            <span className="cc-action-bar-toggle__label">Ocultar menú</span>
          </button>
        </div>
          <ul className="cc-sidebar-links">
               {getCreateCourseSidebarTabs().map(({ key, label }) => (
              <li key={key}>
                <button
                  className={`cc-sidebar-button ${activeTab === key ? "active" : ""}`}
                  onClick={() => setActiveTab(key)}
                >
                  {label}
                </button>
              </li>
            ))}
            <li>
              <button
                type="button"
                className="cc-sidebar-button staff-sidebar-exit-button staff-sidebar-exit-button--discard"
                onClick={handleCancelCourseCreation}
              >
                {getMessage("courseEditor.cancelAndExit")}
              </button>
            </li>
            <li>
              <button type="button" className="cc-sidebar-button guardar-button" onClick={handleGuardarCurso}>
                {getMessage("courseEditor.saveChanges")}
              </button>
            </li>
            <li>
            <button
                className="cc-sidebar-button"
                onClick={() => setMostrarModalBorrarBorrador(true)}
              >
                {/*🗑️*/} Borrar datos Curso
              </button>
            </li>
          </ul>
        </ResponsiveActionSidebar>
        )}

        <main className="cc-content">
          {!actionBarVisible && (
            <button
              type="button"
              className="cc-action-bar-toggle cc-action-bar-toggle--show"
              onClick={() => setActionBarVisible(true)}
              title="Mostrar menú lateral de acciones"
              aria-label="Mostrar menú lateral"
            >
              <span className="cc-action-bar-toggle__icon" aria-hidden="true">
                &#9654;
              </span>
              <span className="cc-action-bar-toggle__label">Mostrar menú</span>
            </button>
          )}
          {mensaje && (
            <p ref={mensajeRef} className="cc-message" role="status" aria-live="polite">
              {mensaje}
            </p>
          )}

          {activeTab === "crearCurso" && (
            <>
              <h1>Datos Principales</h1>
         
              <div className="form-group cc-curso-datos-principales-row">
                    <div className="cc-curso-titulo-field">
                      <label htmlFor="crear-curso-titulo">Título del curso:</label>
                      <input
                        id="crear-curso-titulo"
                        type="text"
                        className="form-control cc-curso-titulo-input"
                        value={titulo}
                        onChange={(e) => setTitulo(e.target.value)}
                        placeholder="Ingrese el título del curso"
                        required
                      />
                    </div>

                    <div className="form-group cc-curso-duracion-field">
                        <label htmlFor="crear-curso-duracion">Duración:</label>
                        <input
                          id="crear-curso-duracion"
                          type="text"
                          className="form-control medio-ancho4"
                          value={duracion}
                          onChange={(e) => setDuracion(e.target.value)}
                          placeholder="Ejemplo: 10 semanas (texto libre)"
                          required
                        />
                      </div>

                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <label htmlFor="crear-curso-imagen">Imagen del curso:</label>
                      <small className="course-image-upload-help">
                        Proporción recomendada 16:9 (por ejemplo, 1600 × 900 px). La imagen se mostrará completa sin deformarse.
                      </small>
                      <input
                        id="crear-curso-imagen"
                        type="file"
                        accept="image/*"
                        onChange={(e) => setImagenCurso(e.target.files?.[0] ?? null)}
                      />
                      {imagenCurso && imagenCursoPreviewUrl && (
                        <div className="imagen-preview-container">
                          <img
                            src={imagenCursoPreviewUrl}
                            alt="Vista previa de la imagen del curso"
                            className="imagen-preview"
                          />
                        </div>
                      )}
                    </div>
                  </div>

              <div className="form-group">
                <label htmlFor="crear-curso-descripcion">Descripción:</label>
                <CourseDescriptionEditor
                  id="crear-curso-descripcion"
                  className="cc-curso-descripcion-input"
                  value={descripcion}
                  onChange={setDescripcion}
                  placeholder="Ingrese la descripción del curso"
                  required
                />
              </div>
                  
              <section className="form-group cc-price-discount-panel" aria-labelledby="crear-curso-price-discount-title">
                <div className="cc-price-discount-panel__header">
                  <h3 id="crear-curso-price-discount-title">Precio y descuento del curso</h3>
                  <p>
                    Configura únicamente los datos comerciales de este curso: precio base, descuento promocional, código y precio final calculado.
                  </p>
                </div>
                <div className="cc-price-discount-grid">
              <div>
                  <label htmlFor="crear-curso-precio">Precio:</label>
                  <input
                    id="crear-curso-precio"
                    type="number"
                    className="form-control medio-ancho1"
                    onWheel={(e) => e.target.blur()} // Evita el scroll accidental
                    value={precio}
                    onChange={(e) => setPrecio(e.target.value)}
                    placeholder="100€"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="crear-curso-descuento">Descuento:</label>
                  <input
                    id="crear-curso-descuento"
                    type="number"
                    className="form-control medio-ancho1"
                    onWheel={(e) => e.target.blur()} // Evita el scroll accidental
                    value={descuento}
                    onChange={(e) => setDescuento(e.target.value)}
                    placeholder="20%"
                  />
                </div>

                <div>
                  <label htmlFor="crear-curso-codigo-descuento">Código:</label>
                  <input
                    id="crear-curso-codigo-descuento"
                    type="text"
                    className="form-control medio-ancho1"
                    value={codigoDescuento}
                    onChange={(e) => setCodigoDescuento(e.target.value)}
                    placeholder="Codigo20%"
                  />
                </div>

                <div>
                  <label htmlFor="crear-curso-precio-final">Precio final:</label>
                  <input
                    id="crear-curso-precio-final"
                    type="text"
                    className="form-control medio-ancho1"
                    value={precioFinal}
                    placeholder="Precio - Descuento"
                    readOnly
                  />
                </div>
                </div>
              </section>

              <div
                className="form-group cc-curso-estado-toggle"
                style={{
                  marginTop: 20,
                  padding: 15,
                  border: "1px solid #e0e0e0",
                  borderRadius: 8,
                  backgroundColor: "#f9f9f9",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <div>
                  <label style={{ display: "flex", alignItems: "center", cursor: "pointer", gap: 15 }}>
                    <div style={{ position: "relative", width: 50, height: 26 }}>
                      <input
                        type="checkbox"
                        checked={esAbierto}
                        onChange={(e) => {
                          setEsAbierto(e.target.checked);
                          setMensaje(
                            e.target.checked
                              ? "Curso en modo abierto. Pulsa «Guardar curso» para crear el curso con este estado."
                              : "Curso en modo cerrado. Pulsa «Guardar curso» para crear el curso con este estado."
                          );
                        }}
                        style={{ opacity: 0, width: 0, height: 0 }}
                      />
                      <span
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          backgroundColor: esAbierto ? "#4caf50" : "#ccc",
                          transition: ".4s",
                          borderRadius: 34,
                        }}
                      >
                        <span
                          style={{
                            position: "absolute",
                            height: 20,
                            width: 20,
                            left: 3,
                            bottom: 3,
                            backgroundColor: "white",
                            transition: ".4s",
                            borderRadius: "50%",
                            transform: esAbierto ? "translateX(24px)" : "translateX(0)",
                            boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                          }}
                        />
                      </span>
                    </div>
                    <div>
                      <span style={{ fontWeight: "bold", fontSize: "1.1rem", color: esAbierto ? "#2e7d32" : "#616161" }}>
                        {esAbierto ? "CURSO ABIERTO" : "CURSO CERRADO"}
                      </span>
                      <p style={{ margin: "5px 0 0 0", fontSize: "0.9rem", color: "#666" }}>
                        {esAbierto
                          ? "Los alumnos pueden acceder a los tests sin completar el temario previo."
                          : "Los alumnos deben completar el temario antes de acceder a los tests (comportamiento por defecto)."}
                      </p>
                    </div>
                  </label>
                </div>
              </div>

              <div className="form-group" style={{ marginTop: 12 }}>
                <label htmlFor="crear-fecha-cierre">Fecha de cierre (opcional):</label>
                <input
                  id="crear-fecha-cierre"
                  type="date"
                  className="form-control medio-ancho4"
                  value={fechaCierre}
                  onChange={(e) => setFechaCierre(e.target.value)}
                />
              </div>

              <CourseDownloadableToggle
                headingId="crear-curso-descarga-heading"
                checked={contenidoDescargable}
                onChange={setContenidoDescargable}
              />

              <div className="form-group">
                <label htmlFor="crear-curso-num-pestanas">
                  <h5 style={{ margin: 0 }}>Número de pestañas del curso:</h5>
                </label>
                <input 
                  id="crear-curso-num-pestanas"
                  type="number"
                  min="1"
                  className= "numero-pestañas"
                  onWheel={(e) => e.target.blur()} // Evita el scroll accidental
                  value={pestanasCurso.length}
                  onChange={(e) => {
                    const cantidad = Number.parseInt(e.target.value, 10) || 0;
                    if (cantidad > 0) {
                      const previous = [...pestanasCurso];
                      const nuevas = [...pestanasCurso];
                      while (nuevas.length < cantidad) nuevas.push("");
                      while (nuevas.length > cantidad) nuevas.pop();
                      const removedNames =
                        previous.length > nuevas.length
                          ? previous.slice(nuevas.length).map((p) => (p ?? "").trim()).filter(Boolean)
                          : [];
                      setPestanasCurso(nuevas);
                      lastUniqueTabKeysRef.current = resizeUniqueTabKeys(lastUniqueTabKeysRef.current, nuevas);
                      if (removedNames.length > 0) {
                        cleanupStateForRemovedCourseTabs(removedNames);
                      }
                    }
                  }}
                />
              </div>

              <div className="nombre-pestanas-grid-scroll">
                  {pestanasCurso.map((nombre, index) => (
                    <div key={index} className="form-group">
                      <label htmlFor={`crear-curso-pestana-${index}`}>Pestaña {index + 1}:</label>
                      <input
                        id={`crear-curso-pestana-${index}`}
                        className="form-control nombre-pestana-input"
                        placeholder="Nombre de la pestaña"
                        type="text"
                        value={nombre}
                        onChange={(e) => {
                          const result = applyCourseTabNameInput(
                            pestanasCurso,
                            index,
                            e.target.value ?? "",
                            lastUniqueTabKeysRef.current,
                          );
                          lastUniqueTabKeysRef.current = result.uniqueKeys;
                          const { tabs: nuevas, oldRaw, remapFrom, remapTo, shouldRemap } = result;
                          setPestanasCurso(nuevas);
                          if (shouldRemap) {
                            const nuevoContenido = { ...bloquesContenido };
                            const sourceKey = findTemarioContentKey(nuevoContenido, [remapFrom, oldRaw]);
                            let contenidoUpdated = false;
                            if (sourceKey && sourceKey !== remapTo) {
                              nuevoContenido[remapTo] = nuevoContenido[sourceKey];
                              delete nuevoContenido[sourceKey];
                              contenidoUpdated = true;
                            }
                            if (contenidoUpdated) {
                              setBloquesContenido(nuevoContenido);
                            }
                            setTests((prev) =>
                              prev.map((t) => ({
                                ...t,
                                pestanas: Array.isArray(t.pestanas)
                                  ? remapPestanaListAfterRename(t.pestanas, remapFrom, remapTo)
                                  : [],
                              })),
                            );
                            if ((bloqueSeleccionado ?? "").trim() === remapFrom || bloqueSeleccionado === oldRaw) {
                              setBloqueSeleccionado(remapTo);
                            }
                          }
                        }}
                        onBlur={() => {
                          if (hasDuplicateCourseTabs(pestanasCurso)) {
                            setMensaje(`⚠️ ${getMessage("courseEditor.duplicateTabsSaveWarning")}`);
                          }
                        }}
                      />
                    </div>
                  ))}
                </div>

                <div className="form-group4">
                <label>
                  <h5>Pestañas del Curso:</h5>
                </label>
                <ul className="lista-pestanas">
                  {pestanasCurso.map((nombre, index) => (
                    <li key={index} className="pestana-item">
                      <span>{nombre}</span>
                      <button
                          onClick={() => {
                            const nombrePestana = pestanasCurso[index];
                            const tieneContenido = bloquesContenido[nombrePestana]?.length > 0;
                          
                            const shouldDelete = tieneContenido
                              ? globalThis.confirm(`La pestaña "${nombrePestana}" contiene contenido. ¿Estás seguro de que deseas eliminarla?`)
                              : true;
                            if (shouldDelete) {
                              const removedName = (nombrePestana ?? "").trim();
                              const nuevas = pestanasCurso.filter((_, i) => i !== index);
                              setPestanasCurso(nuevas);
                              lastUniqueTabKeysRef.current = resizeUniqueTabKeys(lastUniqueTabKeysRef.current, nuevas);
                              if (removedName) {
                                cleanupStateForRemovedCourseTabs([removedName]);
                              }
                            }
                          }}
                        >
                          🗑️
                        </button>

                    </li>
                  ))}
                </ul>
              </div>
             
            </>
          )}

          {activeTab === "crearTest" && (
            <>
            <div className="form-group5" style={{ display: "flex", alignItems: "center", gap: "150px" }}>
              <h1 style={{ margin: 10, fontSize: "2rem" }}>Crear Test</h1>
              <div className="seleccionador">
              <label htmlFor="crear-curso-asignar-pestana" style={{ marginRight: "10px" }}>Asignar a Pestaña:</label>
                <ViewportSelect
                  id="crear-curso-asignar-pestana"
                  value={testActual.pestanas?.[0] || ""}
                  onChange={(e) =>
                    setTestActual({ ...testActual, pestanas: [e.target.value] })
                  }
                >
                  <option value="">-- Selecciona una pestaña --</option>
                  {getNamedCourseTabs(pestanasCurso).map((nombre) => (
                    <option key={nombre} value={nombre}>
                      {nombre}
                    </option>
                  ))}
                </ViewportSelect>
                </div>

                    <div style={{ marginTop: "0px", textAlign: "right" }}>
                    <button
                        className="style1-button"
                        onClick={() => {
                          if (!testActual.titulo.trim()) {
                            setMensaje("⚠️ Debes añadir un título al test.");
                            return;
                          }
                          const preguntasToSave = filterNonBlankTestPreguntas(preguntas);
                          const preguntasInvalidMsg = validateTestPreguntasForSave(preguntasToSave, {
                            allowDraft: true,
                          });
                          if (preguntasInvalidMsg) {
                            setMensaje(preguntasInvalidMsg);
                            return;
                          }
                          const validPestanas = pestanasCurso
                            .map((p) => (p ?? "").trim())
                            .filter(Boolean);
                          if (validPestanas.length === 0) {
                            setMensaje("⚠️ Define al menos una pestaña del curso antes de asignar el test.");
                            setActiveTab("crearCurso");
                            return;
                          }
                          const selectedPestana = (testActual.pestanas?.[0] ?? "").trim();
                          if (!selectedPestana || !validPestanas.includes(selectedPestana)) {
                            setMensaje("⚠️ Selecciona una pestaña válida para el test.");
                            return;
                          }
                        
                          const testCompleto = {
                            ...testActual,
                            preguntas: preguntasToSave,
                          };
                        
                          if (modoEdicion) {
                            const copia = [...tests];
                            copia[indexEdicion] = testCompleto;
                            setTests(copia);
                            setMensaje("✅ Test editado correctamente.");
                          } else {
                            setTests([...tests, testCompleto]);
                            setMensaje("✅ Test añadido al curso.");
                          }
                        
                          // Reset
                          setTestActual({
                            titulo: "",
                            tipo: "temario",
                            tiempo: 0,
                            preguntas: [],
                            pestanas: [],
                          });
                          setPreguntas([
                            {
                              pregunta: "",
                              respuestas: ["", "", "", ""],
                              correcta: null,
                              explicacion: "",
                            },
                          ]);
                          setModoEdicion(false);
                          setIndexEdicion(null);
                          setPreguntaSeleccionada(0);
                        }}
                      >
                        {modoEdicion ? " Guardar Cambios" : "➕ Añadir Test al Curso"}
                        </button>
                      {modoEdicion && (
                          <button
                            className="style3-button"
                            onClick={() => {
                              setModoEdicion(false);
                              setIndexEdicion(null);
                              setTestActual({
                                titulo: "",
                                tipo: "temario",
                                tiempo: 0,
                                preguntas: [],
                                pestanas: [],
                              });
                              setPreguntas([
                                {
                                  pregunta: "",
                                  respuestas: ["", "", "", ""],
                                  correcta: null,
                                  explicacion: "",
                                },
                              ]);
                              setPreguntaSeleccionada(0);
                              setMensaje("✏️ Edición cancelada.");
                            }}
                          >
                            Cancelar Edición
                          </button>
                        )}


                        <button
                            className="style1-button"
                            onClick={() => setMostrarVistaPrevia(true)}
                            >
                            {/*👁️*/}
                            Vista Previa del Test
                          </button>

                    </div>
                  </div>

            <div className="contenedor-creartets" style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
            <div className="form-group">
                <label htmlFor="crear-curso-test-titulo">Título del Test:</label>
                <input
                  id="crear-curso-test-titulo"
                  type="text"
                  className="form-control medio-ancho"
                  value={testActual.titulo}
                  onChange={(e) =>
                    setTestActual({ ...testActual, titulo: e.target.value })
                  }
                  placeholder="Título del test"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="crear-curso-test-tiempo">Tiempo del test (en minutos):</label>
                <input
                  id="crear-curso-test-tiempo"
                  type="number"
                  className="form-control medio-ancho"
                  value={testActual.tiempo}
                  onChange={(e) =>
                    setTestActual({ ...testActual, tiempo: Number.parseInt(e.target.value, 10) || 0 })
                  }
                  placeholder="Ej: 30"
                />
              </div>
              <div className="form-group">
                <label htmlFor="crear-curso-test-por-intento">Preguntas por intento (banco):</label>
                <input
                  id="crear-curso-test-por-intento"
                  type="number"
                  min={1}
                  className="form-control medio-ancho"
                  value={testActual.preguntasPorIntento ?? ""}
                  placeholder="Todas si está vacío"
                  onChange={(e) => {
                    const raw = e.target.value.trim();
                    const parsed = raw === "" ? null : Number.parseInt(raw, 10);
                    setTestActual({
                      ...testActual,
                      preguntasPorIntento:
                        Number.isFinite(parsed) && parsed > 0 ? parsed : null,
                    });
                  }}
                />
              </div>

              <div className="crear-test-container">
                <div className="crear-test-form">
                  <div className="form-group">
                    <label htmlFor="crear-curso-test-pregunta">Pregunta:</label>
                    <textarea
                      id="crear-curso-test-pregunta"
                      className="form-control cc-test-pregunta-input"
                      value={preguntas[preguntaSeleccionada]?.pregunta ?? ""}
                      onChange={(e) => {
                        setPreguntas((prev) => {
                          const base = ensurePreguntaAtIndex(prev, preguntaSeleccionada);
                          const copy = [...base];
                          const cur = copy[preguntaSeleccionada];
                          copy[preguntaSeleccionada] = { ...cur, pregunta: e.target.value };
                          return copy;
                        });
                      }}
                      rows={3}
                      placeholder="Escribe la pregunta aquí (opcional si hay imagen)"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="crear-curso-test-pregunta-imagen">Imagen de la pregunta (opcional):</label>
                    {preguntas[preguntaSeleccionada]?.imagenUrl ? (
                      <img
                        src={preguntas[preguntaSeleccionada].imagenUrl}
                        alt="Imagen de la pregunta"
                        className="imagen-preview"
                        style={{ maxWidth: 280, display: "block", marginBottom: 8 }}
                      />
                    ) : null}
                    <input
                      id="crear-curso-test-pregunta-imagen"
                      type="file"
                      accept="image/*"
                      className="cc-test-file-input"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        const result = await uploadTestQuestionImage(file);
                        if (!result.ok) {
                          setMensaje(`⚠️ ${result.error}`);
                          return;
                        }
                        setPreguntas((prev) => {
                          const base = ensurePreguntaAtIndex(prev, preguntaSeleccionada);
                          const copy = [...base];
                          copy[preguntaSeleccionada] = {
                            ...copy[preguntaSeleccionada],
                            imagenUrl: result.url,
                          };
                          return copy;
                        });
                        setMensaje("Imagen de pregunta subida.");
                      }}
                    />
                  </div>
                    
                  {(preguntas[preguntaSeleccionada]?.respuestas ?? []).map((respuesta, idx) => (
                    <div key={idx} className="form-group2">
                      <label>
                        <input
                          type="radio"
                          name="respuestaCorrecta"
                          checked={preguntas[preguntaSeleccionada]?.correcta === idx}
                          onChange={() => {
                            setPreguntas((prev) => {
                              const base = ensurePreguntaAtIndex(prev, preguntaSeleccionada);
                              const copy = [...base];
                              const cur = copy[preguntaSeleccionada];
                              copy[preguntaSeleccionada] = { ...cur, correcta: idx };
                              return copy;
                            });
                          }}
                        />{" "}
                        Respuesta {idx + 1}
                      </label>
                      <textarea
                        className="form-control cc-test-respuesta-input"
                        value={respuesta}
                        onChange={(e) => {
                          setPreguntas((prev) => {
                            const base = ensurePreguntaAtIndex(prev, preguntaSeleccionada);
                            const copy = [...base];
                            const cur = copy[preguntaSeleccionada];
                            const respuestas = [...(Array.isArray(cur.respuestas) ? cur.respuestas : [...DEFAULT_RESPUESTAS_PLANTILLA])];
                            while (respuestas.length <= idx) {
                              respuestas.push("");
                            }
                            respuestas[idx] = e.target.value;
                            copy[preguntaSeleccionada] = { ...cur, respuestas };
                            return copy;
                          });
                        }}
                        rows={3}
                        placeholder={`Respuesta ${idx + 1}`}
                      />
                    </div>
                  ))}

                  <div className="form-group2">
                    <label htmlFor="crear-curso-test-explicacion">Explicación de la respuesta correcta:</label>
                    <textarea
                      id="crear-curso-test-explicacion"
                      className="form-control cc-test-explicacion-input"
                      value={preguntas[preguntaSeleccionada]?.explicacion ?? ""}
                      onChange={(e) => {
                        setPreguntas((prev) => {
                          const base = ensurePreguntaAtIndex(prev, preguntaSeleccionada);
                          const copy = [...base];
                          const cur = copy[preguntaSeleccionada];
                          copy[preguntaSeleccionada] = { ...cur, explicacion: e.target.value };
                          return copy;
                        });
                      }}
                      rows={5}
                      placeholder="Escribe la explicación aquí..."
                    />
                  </div>
                </div>


                <div className="crear-test-sidebar">
                  <div className="crear-test-sidebar-actions">
                   <button
                    type="button"
                    className="crear-test-sidebar-button"
                    title="Añadir pregunta"
                    onClick={() => {
                      setPreguntas([...preguntas, {
                        pregunta: "",
                        respuestas: ["", "", "", ""],
                        correcta: null,
                        explicacion: "",
                      }]);
                      setPreguntaSeleccionada(preguntas.length);
                    }}
                    >
                    ➕
                    </button>
                   <button
                    type="button"
                    className="crear-test-sidebar-button crear-test-sidebar-button--dup"
                    title="Duplicar la pregunta seleccionada"
                    disabled={preguntas.length === 0}
                    onClick={() => {
                      const actual = preguntas[preguntaSeleccionada];
                      if (!actual) return;
                      const copia = {
                        pregunta: actual.pregunta,
                        respuestas: [...(actual.respuestas || ["", "", "", ""])],
                        correcta: actual.correcta,
                        explicacion: actual.explicacion || "",
                        imagenUrl: actual.imagenUrl || "",
                      };
                      const insertAt = preguntaSeleccionada + 1;
                      const nuevas = [...preguntas.slice(0, insertAt), copia, ...preguntas.slice(insertAt)];
                      setPreguntas(nuevas);
                      setPreguntaSeleccionada(insertAt);
                    }}
                    >
                    ⧉
                    </button>
                   <button
                    type="button"
                    className="crear-test-sidebar-button"
                    title="Eliminar pregunta seleccionada"
                    disabled={preguntas.length <= 1}
                    onClick={() => {
                      if (preguntas.length <= 1) return;
                      if (!confirmDeleteTestQuestion(preguntaSeleccionada)) return;
                      const nuevas = preguntas.filter((_, i) => i !== preguntaSeleccionada);
                      setPreguntas(nuevas);
                      setPreguntaSeleccionada((prev) => Math.max(0, prev - 1));
                    }}
                    >
                    ➖
                   </button>
                  </div>

                  <div className="pregunta-lista">
                   {preguntas.map((row, idx) => (
                      <button
                        type="button"
                        key={`pregunta-${idx}`}
                        className={`cc-pregunta-item cc-pregunta-item-btn ${idx === preguntaSeleccionada ? "is-active" : ""}`}
                        onClick={() => setPreguntaSeleccionada(idx)}
                        title={formatTestQuestionSidebarTitle(row, idx)}
                      >
                        <span className="cc-pregunta-item__index">Pregunta {idx + 1}</span>
                        <span className="cc-pregunta-item__preview">
                          {formatTestQuestionSidebarPreview(row)}
                        </span>
                      </button>
                    ))}
                  </div>
               </div>
              </div>
            </div>
            {tests.length > 0 && (
  <div className="contenedor-lista-tets">
    <h3 style={{ marginBottom: "10px" }}>Tests añadidos:</h3>
    <ul style={{ listStyle: "none", padding: 0 }}>
      {tests.map((test, i) => (
        <li className={`lista-test ${indexEdicion === i ? "test-en-edicion" : ""}`}
          key={`${test.titulo || "test"}-${i}`}
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "8px",
          }}
        >
          <span className="cc-course-test-summary">
            {test.tipo === "temario" ? "📝" : "📘"} <strong>{test.titulo}</strong> — {test.preguntas.length} preguntas — ⏱ {test.tiempo} min
          </span>
          <div className="cc-course-test-actions" style={{ display: "flex", gap: "6px" }}>
              <button
                type="button"
                className="style2-button"
                onClick={() => handleMoveTest(i, -1)}
                disabled={i === 0}
                aria-label={`Subir test ${test.titulo || "sin título"}`}
              >
                ↑
              </button>
              <button
                type="button"
                className="style2-button"
                onClick={() => handleMoveTest(i, 1)}
                disabled={i === tests.length - 1}
                aria-label={`Bajar test ${test.titulo || "sin título"}`}
              >
                ↓
              </button>
              <button
                type="button"
                className="style2-button"
                onClick={() => {
                  setTestAEliminarIndex(i);
                  setMostrarConfirmacion(true);
                }}
              >
                Eliminar
              </button>
              <button
                type="button"
                className="style2-button"
                onClick={() => {
                  const testSeleccionado = tests[i];
                  setTestActual({
                    titulo: testSeleccionado.titulo,
                    tipo: testSeleccionado.tipo,
                    tiempo: testSeleccionado.tiempo,
                    preguntasPorIntento:
                      typeof testSeleccionado.preguntasPorIntento === "number" &&
                      testSeleccionado.preguntasPorIntento > 0
                        ? Math.floor(testSeleccionado.preguntasPorIntento)
                        : null,
                    preguntas: [],
                    pestanas: Array.isArray(testSeleccionado.pestanas) ? testSeleccionado.pestanas : [],
                  });
                  const copiaPreguntas = testSeleccionado.preguntas.map((p) => ({
                    pregunta: p.pregunta,
                    respuestas: [...p.respuestas],
                    correcta: p.correcta,
                    explicacion: p.explicacion,
                    imagenUrl: p.imagenUrl || "",
                  }));
                  setPreguntas(copiaPreguntas);
                  setModoEdicion(true);
                  setIndexEdicion(i);
                  setMensaje("✏️ Editando test seleccionado...");
                }}
              >
                Editar
              </button>
              <button
                type="button"
                className="style2-button"
                onClick={() => {
                  setTestParaVistaPrevia(tests[i]); // 👈 test individual
                }}
              >
                Vista Previa
              </button>
            </div>
        </li>
      ))}
    </ul>
  </div>
)}

           </>
          )}


              {activeTab === "subirDocumento" && (
                <div className="cc-doc-upload-panel">
                  <CourseDownloadableToggle
                    headingId="crear-curso-subir-descarga-heading"
                    checked={contenidoDescargable}
                    onChange={setContenidoDescargable}
                  />
                  <p style={{ fontSize: "0.85rem", color: "#666", marginTop: -8, marginBottom: 16 }}>
                    Esta opción se envía al crear el curso en la última pestaña (&quot;Crear curso&quot;).
                  </p>
                 <div className="cc-doc-upload-header">
                   <h1>Subir documento</h1>
                     <button
                       type="button"
                       className="style1-button cc-doc-primary-btn"
                       disabled={
                         uploadStatus === "reading" ||
                         uploadStatus === "uploading" ||
                         pestanasCurso.length === 0 ||
                         bloqueSeleccionado === "" ||
                         !(archivoSeleccionado instanceof File)
                       }
                       onClick={() => {
                         void handleAddDoc();
                       }}
                     >
                    Añadir al curso
                    </button>
                  </div>

              <p className="cc-doc-hint">
                Estructura: cada <strong>pestaña</strong> tiene un temario en árbol (bloque, tema, unidad — usa solo los niveles que necesites).
                Puedes crear nodos del temario sin archivos y asociar documentos después. Formatos: PDF, Office, audio y vídeo.
              </p>

              {bloqueSeleccionado ? (
                <section className="cc-doc-temario-editor-section" style={{ marginBottom: 20 }}>
                  <h3>Temario de la pestaña «{bloqueSeleccionado}»</h3>
                  <p className="cc-doc-hint" style={{ marginBottom: 12 }}>
                    Añade bloques, temas o unidades sin subir archivos todavía. Los documentos se pueden enlazar en el formulario de abajo.
                  </p>
                  <TemarioContenidoEditor
                    nodes={bloquesContenido[bloqueSeleccionado] || []}
                    onNodesChange={(next) => {
                      setBloquesContenido((prev) => ({ ...prev, [bloqueSeleccionado]: next }));
                    }}
                    onArchivoRemove={(path, archivoIndex) => {
                      removeArchivoFromTemarioState({
                        contentDisabled: false,
                        bloqueSeleccionado,
                        bloquesContenido,
                        path,
                        archivoIndex,
                        setBloquesContenido,
                        setMensaje,
                        savedHint: 'Pulsa "Guardar cambios" para conservar los cambios.',
                      });
                    }}
                    disabled={false}
                    selectedTabLabel={bloqueSeleccionado}
                    courseTests={tests}
                  />
                  <AdminDocsTestsByTab
                    tests={tests}
                    pestanasCurso={pestanasCurso}
                    contenido={bloquesContenido}
                    onTestsChange={setTests}
                    disabled={false}
                  />
                </section>
              ) : null}

              <div className="cc-doc-form-grid">
              <div className="form-group cc-doc-field">
                <label htmlFor="cc-doc-file">Archivo</label>
                <input
                  id="cc-doc-file"
                  className="form-control"
                  type="file"
                  accept={DOC_UPLOAD_ACCEPT_ATTR}
                  disabled={uploadStatus === "reading" || uploadStatus === "uploading"}
                  onChange={(e) => {
                    handleSelectedDocFile(e.target.files?.[0] || null);
                    e.target.value = "";
                  }}
                />
                <p className="cc-edit-docs-file-summary" aria-live="polite">
                  {archivoSeleccionado instanceof File ? (
                    <>
                      Listo para subir: <strong>{archivoSeleccionado.name}</strong>
                    </>
                  ) : (
                    <span className="cc-edit-docs-file-summary-empty">Sin archivo seleccionado todavía.</span>
                  )}
                </p>
                {uploadStatus !== "idle" && (
                  <div className="cc-docs-upload-progress">
                    <div className="cc-docs-upload-progress-track">
                      <div className="cc-docs-upload-progress-fill" style={{ width: `${uploadProgress}%` }} />
                    </div>
                    <p className="cc-docs-upload-progress-text">
                      {uploadPhaseMessage(uploadStatus, uploadProgress, lastUploadError)}
                    </p>
                    {uploadStatus === "error" && (
                      <button
                        type="button"
                        className="style2-button"
                        onClick={() => {
                          void handleAddDoc(lastAttemptedFileRef.current);
                        }}
                        disabled={!lastAttemptedFileRef.current}
                      >
                        Reintentar subida
                      </button>
                    )}
                  </div>
                )}
              </div>

              <div className="form-group cc-doc-field">
                <label htmlFor="cc-doc-bloque">Pestaña del curso</label>
               <ViewportSelect
                  id="cc-doc-bloque"
                  className="form-select"
                  value={bloqueSeleccionado}
                  onChange={(e) => setBloqueSeleccionado(e.target.value)}
                >
                  <option value="">-- Selecciona una pestaña --</option>
                  {getNamedCourseTabs(pestanasCurso).map((nombre) => (
                    <option key={nombre} value={nombre}>
                      {nombre}
                    </option>
                  ))}
                </ViewportSelect>
              </div>

              <div className="form-group cc-doc-field">
                <label htmlFor="cc-doc-tipo-sub">Destino del archivo en la pestaña</label>
                <ViewportSelect id="cc-doc-tipo-sub" className="form-select" value={tipoInsert} onChange={(e) => setTipoInsert(e.target.value)}>
                  <option value="nuevo">Nuevo nodo en el temario</option>
                  <option value="existente">Ampliar un nodo ya existente</option>
                </ViewportSelect>
              </div>

              {tipoInsert === "nuevo" ? (
                <>
                  <div className="form-group cc-doc-field cc-doc-field--full">
                    <label htmlFor="cc-doc-padre-temario">Colgar el nuevo nodo bajo</label>
                    <ViewportSelect
                      id="cc-doc-padre-temario"
                      className="form-select"
                      value={rutaPadreKey}
                      onChange={(e) => setRutaPadreKey(e.target.value)}
                      disabled={!bloqueSeleccionado}
                    >
                      <option value="">Raíz de la pestaña</option>
                      {listPathOptions(bloquesContenido[bloqueSeleccionado] || [], TEMARIO_DEPTH_LABELS).map((o) => (
                        <option key={o.pathKey} value={o.pathKey}>{o.label}</option>
                      ))}
                    </ViewportSelect>
                  </div>
                  <div className="form-group cc-doc-field cc-doc-field--full">
                    <label htmlFor="cc-doc-nombre-sub">Nombre del nuevo nodo</label>
                    <input id="cc-doc-nombre-sub" className="form-control" type="text" value={subseccion} onChange={(e) => setSubseccion(e.target.value)} placeholder="Ej. Tema 1 — Introducción" />
                  </div>
                </>
              ) : (
                <div className="form-group cc-doc-field cc-doc-field--full">
                  <label htmlFor="cc-doc-sub-exist">Nodo del temario</label>
                  <ViewportSelect
                    id="cc-doc-sub-exist"
                    className="form-select"
                    value={rutaDestinoKey}
                    onChange={(e) => setRutaDestinoKey(e.target.value)}
                    disabled={!bloqueSeleccionado}
                  >
                    <option value="">-- Selecciona --</option>
                    {listPathOptions(bloquesContenido[bloqueSeleccionado] || [], TEMARIO_DEPTH_LABELS).map((o) => (
                      <option key={o.pathKey} value={o.pathKey}>{o.label}</option>
                    ))}
                  </ViewportSelect>
                </div>
              )}

              <div className="form-group cc-doc-field">
                <label htmlFor="cc-doc-posicion">Posición del archivo en el nodo</label>
                <ViewportSelect id="cc-doc-posicion" className="form-select" value={posicion} onChange={(e) => setPosicion(e.target.value)}>
                  <option value="debajo">Al final (debajo)</option>
                  <option value="encima">Al inicio (encima)</option>
                </ViewportSelect>
              </div>
              </div>

                            <div className="cc-doc-preview-box vista-contenido-documentos">
                            <h3>Vista previa del contenido</h3>
                            {pestanasCurso.length > 0 ? (
                              pestanasCurso.map((nombrePestana, i) => {
                                const subsecciones = bloquesContenido[nombrePestana] || [];
                                return subsecciones.length > 0 ? (
                                  <div key={i} className="cc-doc-preview-block">
                                    <h4>{nombrePestana}</h4>
                                    <TemarioContenidoPreview nodes={subsecciones} />
                                  </div>
                                ) : null;
                              })
                            ) : (
                              <p className="cc-doc-preview-empty">No hay pestañas creadas todavía. Define pestañas en «Datos principales».</p>
                            )}
                          </div>
                </div>
                    )}

              {mostrarConfirmacion && (
                <div className="modal-confirmacion">
                  <div className="modal-contenido">
                    <p>¿Estás seguro de que deseas eliminar este test?</p>
                    <div className="modal-botones">
                      <button
                        onClick={() => {
                          const nuevos = [...tests];
                          nuevos.splice(testAEliminarIndex, 1);
                          setTests(nuevos);
                          setMostrarConfirmacion(false);
                          setTestAEliminarIndex(null);
                        }}
                      >
                        Sí, eliminar
                      </button>
                      <button
                        onClick={() => {
                          setMostrarConfirmacion(false);
                          setTestAEliminarIndex(null);
                        }}
                      >
                        Cancelar
                      </button>
                    </div>
                  </div>
                </div>
              )}
              {testParaVistaPrevia && (
                <CourseTestPreviewModal
                  test={testParaVistaPrevia}
                  title="Vista previa del test"
                  onClose={() => setTestParaVistaPrevia(null)}
                />
              )}

              {mostrarVistaPrevia && (
                <CourseTestPreviewModal
                  test={{ ...testActual, preguntas }}
                  title="Vista previa del test"
                  onClose={() => setMostrarVistaPrevia(false)}
                />
              )}
          
          {mostrarModalBorrarBorrador && (
                <div className="modal-confirmacion" onClick={() => setMostrarModalBorrarBorrador(false)}>
                  <div className="modal-contenido" onClick={(e) => e.stopPropagation()}>
                    <p>¿Estás seguro de que deseas borrar todos los datos del curso?</p>
                    <div className="modal-botones">
                      <button
                        onClick={() => {
                          skipBorradorPersistRef.current = true;
                          localStorage.removeItem("borradorCurso");
                          setTitulo("");
                          setDescripcion("");
                          setDuracion("");
                          setPrecio("");
                          setDescuento("");
                          setCodigoDescuento("");
                          setPrecioFinal("");
                          setTests([]);
                          setBloquesContenido({});
                          setPestanasCurso([]);
                          lastUniqueTabKeysRef.current = [];
                          setEsAbierto(false);
                          setFechaCierre("");
                          setContenidoDescargable(false);
                          setImagenCurso(null);
                          setArchivoSeleccionado(null);
                          setBloqueSeleccionado("");
                          setSubseccion("");
                          setRutaDestinoKey("");
                          setRutaPadreKey("");
                          setTestActual({
                            titulo: "",
                            tipo: "temario",
                            tiempo: 0,
                            preguntas: [
                              {
                                pregunta: "",
                                respuestas: ["", "", "", ""],
                                correcta: null,
                                explicacion: "",
                              },
                            ],
                            pestanas: [],
                          });
                          setPreguntas([
                            {
                              pregunta: "",
                              respuestas: ["", "", "", ""],
                              correcta: null,
                              explicacion: "",
                            },
                          ]);
                          setPreguntaSeleccionada(0);
                          setModoEdicion(false);
                          setIndexEdicion(null);
                          setMensaje("🗑️ Borrador eliminado correctamente.");
                          setMostrarModalBorrarBorrador(false);
                        }}
                      >
                        Sí, borrar
                      </button>
                      <button onClick={() => setMostrarModalBorrarBorrador(false)}>
                        Cancelar
                      </button>
                    </div>
                  </div>
                </div>
              )}


                {activeTab === "vistaGeneral" && (
                    <div className="contenedor-creartets cc-vista-general-wrap">
                      <div className="cc-vista-general-header">
                        <h1>Vista general</h1>
                        <p className="cc-vista-general-sub">
                          Resumen del borrador: mismos colores y tipografía que el resto del asistente.
                        </p>
                      </div>

                      <div className="cc-vista-general-preview-actions">
                        <button type="button" className="cc-vista-general-preview-btn" disabled>
                          {getMessage("courseEditor.openStudentPreview")}
                        </button>
                        <p className="cc-doc-hint">{getMessage("courseEditor.previewRequiresSave")}</p>
                      </div>

                      <div className="vista-general-container cc-vista-general-panel">
                        <div className="cc-vista-general-datos">
                          <h2 className="cc-vista-general-titulo">{titulo || "(Sin título)"}</h2>
                          {descripcion ? (
                            <CourseDescriptionContent
                              html={descripcion}
                              className="cc-vista-general-desc"
                            />
                          ) : null}
                          <p className="cc-vista-general-meta">
                            <span>Precio: <strong>{precio || "—"} €</strong></span>
                            <span>Descuento: <strong>{descuento !== "" ? `${descuento} %` : "—"}</strong></span>
                            <span>Pestañas: <strong>{pestanasCurso.filter((p) => (p ?? "").trim()).length}</strong></span>
                            <span>Tests: <strong>{tests.length}</strong></span>
                          </p>
                        </div>

                      {pestanasCurso.filter((p) => (p ?? "").trim()).length > 0 ? (
                        <nav className="vista-cursos-admin-tabs cc-vista-general-tabs" aria-label="Pestañas del borrador">
                          {pestanasCurso.filter((p) => (p ?? "").trim()).map((p) => (
                            <button
                              key={p}
                              type="button"
                              className={p === pestanaVistaPrevia ? "active" : ""}
                              onClick={() => setPestanaVistaPrevia(p)}
                            >
                              {p}
                            </button>
                          ))}
                        </nav>
                      ) : (
                        <p className="cc-doc-preview-empty">Añade pestañas en «Datos principales» para ver el contenido por bloque.</p>
                      )}

                      {pestanaVistaPrevia && (pestanasCurso.filter((p) => (p ?? "").trim()).length > 0) && (
                        <div className="cc-vista-general-tab-body">
                          <h3 className="cc-vista-general-tab-title">{pestanaVistaPrevia}</h3>
                          {(bloquesContenido[pestanaVistaPrevia]?.length > 0) ? (
                            <TemarioContenidoPreview nodes={bloquesContenido[pestanaVistaPrevia]} />
                          ) : (
                            <p className="cc-doc-preview-empty">No hay documentos en esta pestaña.</p>
                          )}
                          <h4 className="cc-vista-general-tests-title">Tests</h4>
                          <ul className="cc-vista-general-tests-list">
                            {tests.filter((t) => Array.isArray(t.pestanas) && t.pestanas.includes(pestanaVistaPrevia)).length === 0 ? (
                              <li>No hay tests en esta pestaña.</li>
                            ) : (
                              tests
                                .filter((t) => Array.isArray(t.pestanas) && t.pestanas.includes(pestanaVistaPrevia))
                                .map((test, idx) => (
                                  <li key={idx}>
                                    {test.titulo} — {(test.preguntas || []).length} preguntas
                                  </li>
                                ))
                            )}
                          </ul>
                        </div>
                      )}
                      </div>
                    </div>
                  )}


                {activeTab === "listadodescuentos" && (
                  <EditarCursoDescuentosTab
                    codigoDescuento={codigoDescuento}
                    descuento={descuento}
                    precio={precio}
                    precioFinal={precioFinal}
                  />
                )}

                {activeTab === "testimonios" && (
                  <div style={{ padding: "20px" }}>
                    <h2>Testimonios</h2>
                    <div style={{ 
                      padding: "20px", 
                      backgroundColor: "rgba(255, 193, 7, 0.12)", 
                      borderRadius: "8px", 
                      border: "1px solid #ffc107",
                      marginTop: "16px"
                    }}>
                      <p style={{ margin: "0 0 12px", fontWeight: "600", color: "#f57c00" }}>
                        📋 Los testimonios solo pueden añadirse después de guardar el curso
                      </p>
                      <p style={{ margin: 0, color: "rgba(255, 255, 255, 0.9)" }}>
                        Guarda primero este curso usando el botón "Guardar cambios". 
                        Luego podrás añadir y gestionar testimonios desde la pantalla de Editar Curso.
                      </p>
                    </div>
                  </div>
                )}

          </main>
      </div>
    </div>

 
  );
};

export default CrearCurso;
